import { Card, Button, Icon, Typography, Loader, LabelValue, Separator, Checkbox } from '@design-system/index';
import {
    ArchiveFilled,
    ArchiveLine,
    RoundedArrow,
    SettingsFocus,
    UnarchiveFilled,
    UnarchiveLine,
    SettingsNormal,
    PrinterLine,
    PrinterFull
} from '@assets/images/icons';
import '../../DetailsPage.scss';
import { Grid } from '@design-system/components/Grid/Grid';
import { formatDate } from '@utils/dates';
import { Table } from '@components/Table/Table/Table';
import { ColumnDef } from '@tanstack/react-table';
import { Contribution, FinancialBenefitSchedule, SocialBenefit } from '@services/member/member/member.types';
import moment from 'moment';
import { formatMoney } from '@utils/numbers';
import { CONTRIBUTION_TYPE, FAMILY_STATUS, PAYMENT_TYPE } from '@utils/enumTranslation';
import { useDetailsPreviewVM } from './useDetailsPreviewVM';
import { generatePrintHTML } from './DetailsPreviewPrint';
import { openPrintWindow } from '@utils/printUtils';
import { Booking } from '@services/member/booking/booking.types';

export default function DetailsPreview({ setActiveTab }: { setActiveTab: (tab: number) => void }) {
    const {
        handleResetPassword,
        isLoading,
        isError,
        member,
        bookingColumnHelper,
        socialBenefitColumnHelper,
        financialBenefitScheduleColumnHelper,
        contributionColumnHelper,
        handleToggleArchiveMember
    } = useDetailsPreviewVM();

    const handlePrint = async () => {
        if (!member) {
            console.error('Aucun membre à imprimer');
            return;
        }

        try {
            const printHtml = generatePrintHTML(member);
            await openPrintWindow(printHtml, {
                title: `Fiche Adhérent - ${member.fullname}`,
                width: 800,
                height: 600
            });
        } catch (error) {
            console.error("Erreur lors de l'impression:", error);
        }
    };

    const socialBenefitsColumns = [
        socialBenefitColumnHelper.accessor('assignedAt', {
            header: 'Attribué le',
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')}</>
        }),
        socialBenefitColumnHelper.accessor('recipientFullname', {
            id: 'child',
            header: 'Bénéficiaire',
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        socialBenefitColumnHelper.accessor('amount', {
            header: 'Montant',
            cell: (info) => <>{formatMoney(info.getValue())} </>
        }),
        socialBenefitColumnHelper.accessor('label', {
            header: 'Intitulé',
            cell: (info) => <>{info.getValue()} </>
        }),
        socialBenefitColumnHelper.accessor('observation', {
            header: 'Commentaire',
            enableSorting: false,
            cell: (info) => <>{info.getValue()}</>
        })
    ] as ColumnDef<SocialBenefit>[];

    const financialBenefitSchedulesColumns = [
        financialBenefitScheduleColumnHelper.accessor('scheduledAt', {
            id: 'year',
            header: 'Année',
            cell: (info) => <>{moment(info.getValue()).format('YYYY')}</>
        }),
        financialBenefitScheduleColumnHelper.accessor('scheduledAt', {
            id: 'month',
            header: 'Mois',
            cell: (info) => <>{moment(info.getValue()).format('MMMM')}</>
        }),
        financialBenefitScheduleColumnHelper.accessor('total', {
            header: 'Montant',
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        financialBenefitScheduleColumnHelper.accessor('isPointed', {
            header: 'Pointage',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={info.getValue()} onChange={() => {}} />
                </div>
            )
        })
    ] as ColumnDef<FinancialBenefitSchedule>[];

    const contributionsColumns = [
        contributionColumnHelper.accessor('year', {
            header: 'Année',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ display: 'flex', justifyContent: 'center' }}>{info.getValue()}</div>
        }),
        contributionColumnHelper.accessor('dateContributed', {
            header: 'Date de paiement',
            cell: (info) => {
                const value = info.getValue();
                return <>{value ? moment(value).format('DD/MM/YYYY') : ''}</>;
            }
        }),
        contributionColumnHelper.accessor('amount', {
            header: 'Montant',
            cell: (info) => <>{formatMoney(info.getValue())} </>
        }),
        contributionColumnHelper.accessor('paymentType', {
            header: 'Nature',
            cell: (info) => <>{PAYMENT_TYPE[info.getValue()]} </>
        }),
        contributionColumnHelper.accessor('observation', {
            header: 'Commentaire',
            cell: (info) => <>{info.getValue()} </>
        })
    ] as ColumnDef<Contribution>[];

    const bookingsColumns = [
        bookingColumnHelper.accessor('apartment.fullname', {
            id: 'apartment.fullname',
            header: 'Logement',
            cell: (info) => <>{info.getValue()} </>
        }),
        bookingColumnHelper.accessor('arrivalDate', {
            header: "Date d'arrivée",
            id: 'arrivalDate',
            cell: (info) => <>{formatDate(info.getValue())} </>
        }),
        bookingColumnHelper.accessor('departureDate', {
            header: 'Date de départ',
            id: 'departureDate',
            cell: (info) => <>{formatDate(info.getValue())} </>
        }),
        bookingColumnHelper.accessor('peopleCount', {
            id: 'peopleCount',
            header: 'Nombre de personnes',
            cell: (info) => <>{info.getValue()} </>
        }),
        bookingColumnHelper.accessor('rentalPrice', {
            id: 'rentalPrice',
            header: 'Prix',
            cell: (info) => <>{formatMoney(info.getValue())} </>
        })
    ] as ColumnDef<Booking>[];

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche adhérent
            </Typography>
        );
    }

    if (!member) {
        return <></>;
    }

    return (
        <div className="details-member-container">
            <div className="details-member-container-body">
                <Card
                    headerType="very-light"
                    headerTitle={member.fullname}
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(1)}
                        />
                    }
                >
                    <Grid columns={4}>
                        <LabelValue label="N°COSEM" value={member.cosemNumber} />
                        <LabelValue label="Matricule" value={member.administrativeInformation.registrationNumber} />
                        <LabelValue label="Ancien matricule" value={member.administrativeInformation.oldRegistrationNumber} />
                        <LabelValue label="Organisme" value={member.administrativeInformation.organizationLabel} />
                        <LabelValue label="Code Etat" value={member.administrativeInformation.stateCodeLabel} />
                        <LabelValue label="Service" value={member.administrativeInformation.serviceCodeLabel} />
                        <LabelValue
                            label="Type de cotisation"
                            value={CONTRIBUTION_TYPE[member.administrativeInformation.contributionType]}
                        />
                        <LabelValue label="Date d'adhésion" value={formatDate(member.administrativeInformation.registeredAt)} />
                        <LabelValue
                            label="Date de sortie"
                            value={
                                member.administrativeInformation.unregisteredAt
                                    ? formatDate(member.administrativeInformation.unregisteredAt)
                                    : ''
                            }
                        />
                    </Grid>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Informations importantes"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(1)}
                        />
                    }
                >
                    <Grid columns={3}>
                        <LabelValue label="Nom" value={member.personalInformation.lastname.toUpperCase()} />
                        <LabelValue label="Prénom" value={member.personalInformation.firstname.toUpperCase()} />
                        <LabelValue label="Nom de naissance" value={member.personalInformation.birthName?.toUpperCase()} />
                        <LabelValue label="Date de naissance" value={formatDate(member.personalInformation.birthDate)} />
                        <LabelValue label="Lieu de naissance" value={member.personalInformation.birthPlace} />
                        <LabelValue label="Situation familiale" value={FAMILY_STATUS[member.personalInformation.familyStatus]} />
                    </Grid>
                    <Separator large />
                    <Grid columns={3}>
                        <LabelValue label="N° Téléphone" value={member.contactInformation.phone} />
                        <LabelValue label="Mail" value={member.contactInformation.email} />
                        <LabelValue label="Adresse" value={member.contactInformation.address} />
                        {member.contactInformation.additionalAddress && (
                            <LabelValue label="Complément d'adresse" value={member.contactInformation.additionalAddress} />
                        )}
                        <LabelValue label="Code postal" value={member.contactInformation.zipCode} />
                        <LabelValue label="Ville" value={member.contactInformation.city} />
                    </Grid>
                    <Separator large />
                    <Grid columns={3}>
                        <LabelValue label="Nombre d'enfants" value={member.children.length} />
                        <LabelValue label="Enfant(s) à charge" value={member.children.filter((child) => child.dependent).length} />
                        <LabelValue label="Enfant(s) décédé(s)" value={member.children.filter((child) => child.deceased).length} />
                    </Grid>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Commentaire principal"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(1)}
                        />
                    }
                >
                    <Typography intrinsic="p" variant="big" color="grey1" preLine>
                        {member.observation || 'Aucun commentaire'}
                    </Typography>
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Dernières prestations sociales"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(3)}
                        />
                    }
                >
                    {member.lastSocialBenefits.length > 0 ? (
                        <Table data={member.lastSocialBenefits} columns={socialBenefitsColumns} />
                    ) : (
                        <Typography intrinsic="p" color="secondary">
                            Aucune prestation sociale
                        </Typography>
                    )}
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Prestations financières en cours"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(4)}
                        />
                    }
                >
                    {member.lastFinancialBenefit ? (
                        <Grid columns={2}>
                            <Card headerType="very-light" showHeader={false}>
                                <Grid columns={3}>
                                    <LabelValue label="Code" value={member.lastFinancialBenefit.code} />
                                    <LabelValue label="Demandé le" value={formatDate(member.lastFinancialBenefit.requestedAt)} />
                                    {member.lastFinancialBenefit.financialBenefitSchedules[0] && (
                                        <LabelValue
                                            label="Première échéance"
                                            value={formatDate(member.lastFinancialBenefit.financialBenefitSchedules[0].scheduledAt)}
                                        />
                                    )}
                                    <LabelValue label="Montant du prêt" value={formatMoney(member.lastFinancialBenefit.amount)} />
                                    <LabelValue label="Total" value={formatMoney(member.lastFinancialBenefit.total)} />
                                    <LabelValue
                                        label="Nb d'échéances"
                                        value={member.lastFinancialBenefit.financialBenefitSchedules.length}
                                    />
                                    {member.lastFinancialBenefit.financialBenefitSchedules[0] && (
                                        <LabelValue
                                            label="Montant mensuel"
                                            value={formatMoney(member.lastFinancialBenefit.financialBenefitSchedules[0].amount)}
                                        />
                                    )}
                                </Grid>
                            </Card>
                            <Table
                                data={member.lastFinancialBenefit.nextFinancialBenefitSchedules}
                                columns={financialBenefitSchedulesColumns}
                            />
                        </Grid>
                    ) : (
                        <Typography intrinsic="p" color="secondary">
                            Aucune prestation financière en cours
                        </Typography>
                    )}
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Dernières cotisations"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(5)}
                        />
                    }
                >
                    {member.lastContributions.length > 0 ? (
                        <Table data={member.lastContributions} columns={contributionsColumns} />
                    ) : (
                        <Typography intrinsic="p" color="secondary">
                            Aucune cotisation
                        </Typography>
                    )}
                </Card>
                <Card
                    headerType="very-light"
                    headerTitle="Dernières réservations"
                    headerAction={
                        <Button
                            variant="icon-hover"
                            color="grey-secondary"
                            icon={<Icon icon={<RoundedArrow />} size={24} />}
                            onClick={() => setActiveTab(6)}
                        />
                    }
                >
                    {member.lastBookings.length > 0 ? (
                        <Table data={member.lastBookings} columns={bookingsColumns} />
                    ) : (
                        <Typography intrinsic="p" color="secondary">
                            Aucune réservation
                        </Typography>
                    )}
                </Card>
            </div>
            <div className="details-member-container-footer">
                {member.wordpressId ? (
                    <Button
                        variant="icon"
                        color="primary"
                        size="small"
                        hoverIcon={<Icon icon={<SettingsFocus />} size={20} color="primary" />}
                        icon={<Icon icon={<SettingsNormal />} size={20} color="primary" />}
                        onClick={() => handleResetPassword()}
                    >
                        <Typography intrinsic="p" variant="p2" color="primary">
                            Réinitialiser le mot de passe
                        </Typography>
                    </Button>
                ) : (
                    <div></div>
                )}

                <Button
                    variant="icon"
                    color="primary"
                    size="small"
                    hoverIcon={<Icon icon={<PrinterFull />} size={20} color="primary" />}
                    icon={<Icon icon={<PrinterLine />} size={20} color="primary" />}
                    onClick={handlePrint}
                >
                    <Typography intrinsic="p" variant="p2" color="primary">
                        Imprimer l'aperçu
                    </Typography>
                </Button>

                <Button
                    variant="icon"
                    color="primary"
                    size="small"
                    hoverIcon={
                        member.isArchived ? (
                            <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                        ) : (
                            <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                        )
                    }
                    icon={
                        member.isArchived ? (
                            <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                        ) : (
                            <Icon icon={<ArchiveLine />} size={20} color="primary" />
                        )
                    }
                    onClick={() => handleToggleArchiveMember()}
                >
                    <Typography intrinsic="p" variant="p2" color="primary">
                        {member.isArchived ? 'Désarchiver' : 'Archiver'}
                    </Typography>
                </Button>
            </div>
        </div>
    );
}
